import { TimeZone } from "@qendo/util";
import type { DateArg } from "date-fns";
import { type Dispatch, type SetStateAction, useCallback, useMemo } from "react";
import { ComboBox, type ComboBoxProps, type ItemType } from "~/components/form/combo-box/ComboBox";
import { currentTimezone, formatTimezoneDetails, formatTimezoneName, sortTimeZones } from "~/util/dates";

type TimeZoneItem = {
    value: TimeZone;
    label: string;
    subtitle: string;
} & ItemType<TimeZone>;

export type TimezoneSelectorProps = {
    value: TimeZone | null;
    onItemSelected: ((v: TimeZone | null) => void) | Dispatch<SetStateAction<TimeZone | null>>;
    searchable?: true;
    addable?: never;
    onAddItem?: never;
    date?: DateArg<Date> | null;
} & Partial<ComboBoxProps<TimeZone, TimeZoneItem>>;

export function TimezoneSelector({
    label = "Time zone",
    value,
    onItemSelected,
    date,
    ...comboProps
}: TimezoneSelectorProps) {
    const current_timezone = currentTimezone();
    const items = useMemo(() => sortTimeZones(Object.values(TimeZone), current_timezone), [current_timezone]);
    const mapTimezones = useCallback(
        (item: TimeZone) => ({
            value: item,
            label: formatTimezoneName(item),
            subtitle: formatTimezoneDetails(item, current_timezone, date),
        }),
        [current_timezone, date],
    );

    return (
        <ComboBox<TimeZone>
            {...comboProps}
            value={value}
            onItemSelected={onItemSelected}
            items={items}
            searchField="value"
            mapItems={mapTimezones}
            label={label}
            searchable
            addable={false}
            onAddItem={undefined}
            hint={value !== current_timezone && `Current time zone: ${formatTimezoneName(current_timezone)}`}
        />
    );
}
